import { isValidEmail } from '../../utils/email-utils';
import { conditionallyPluralize, equalsIgnoreCase } from '../../utils/text-utils';
import { CREATIVITY_OPTIONS, DEFAULT_CREATIVITY } from './creativity-options';
import {
  AuditConfig,
  AuditConfigDraft,
  AuditConfigInput,
  AuditConfigRowWire,
  AuditConfigWireBody,
  AuditModelConfig,
  AuditSchedule,
  AuditScheduleDraft,
  DAY_OF_WEEK_SHORT_LABELS,
  DEFAULT_DAY_OF_MONTH,
  DEFAULT_DAYS_OF_WEEK,
  DEFAULT_LOOKBACK_DAYS,
  DEFAULT_MAX_ADS,
  MAX_ADS_LIMIT,
  DEFAULT_MIN_CONVERSIONS,
  DEFAULT_MIN_ACTIVE_DAYS,
  DEFAULT_MIN_SPEND,
  DEFAULT_SCHEDULE_TIME,
  DEFAULT_SCHEDULE_TIMEZONE,
  MAX_CONFIG_NAME_LENGTH,
  SCHEDULE_TIME_OPTIONS,
  ScheduleFrequency,
  TIMEZONE_OPTIONS
} from './audit-config-constants';

// ---- Schedule ----

export const DEFAULT_SCHEDULE: AuditSchedule = {
  daysOfWeek: DEFAULT_DAYS_OF_WEEK,
  frequency: ScheduleFrequency.WEEKLY,
  time: DEFAULT_SCHEDULE_TIME,
  timezone: DEFAULT_SCHEDULE_TIMEZONE
};

// Local calendar date as YYYY-MM-DD (used to prefill the every-N-days anchor).
export const todayIsoDate = (date: Date = new Date()): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

// True for a real calendar date in YYYY-MM-DD form (rejects 2026-02-30 and the like).
export const isRealIsoDate = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
};

export const scheduleToDraft = (
  schedule: AuditSchedule = DEFAULT_SCHEDULE
): AuditScheduleDraft => ({
  anchorDate: schedule.anchorDate ?? '',
  dayOfMonth: String(schedule.dayOfMonth ?? DEFAULT_DAY_OF_MONTH),
  daysOfWeek: schedule.daysOfWeek ?? DEFAULT_DAYS_OF_WEEK,
  frequency: schedule.frequency,
  intervalDays: schedule.intervalDays == null ? '' : String(schedule.intervalDays),
  time: schedule.time,
  timezone: schedule.timezone
});

export const scheduleFromDraft = (draft: AuditScheduleDraft): AuditSchedule => {
  const base = { time: draft.time, timezone: draft.timezone };
  const days = [...draft.daysOfWeek].sort((x, y) => x - y);
  switch (draft.frequency) {
    case ScheduleFrequency.WEEKLY:
      return { ...base, daysOfWeek: days, frequency: draft.frequency };
    case ScheduleFrequency.BIWEEKLY:
      return {
        ...base,
        anchorDate: draft.anchorDate,
        daysOfWeek: days,
        frequency: draft.frequency
      };
    case ScheduleFrequency.MONTHLY:
      return { ...base, dayOfMonth: Number(draft.dayOfMonth), frequency: draft.frequency };
    default:
      return {
        ...base,
        anchorDate: draft.anchorDate,
        frequency: draft.frequency,
        intervalDays: Number(draft.intervalDays)
      };
  }
};

// Mirrors the lambda's rules: 30-minute grid, weekly needs >= 1 day, every-N-days needs an
// integer interval >= 1 and a real anchor date. Returns a message, or undefined when valid.
export const validateScheduleDraft = (draft: AuditScheduleDraft): string | undefined => {
  if (!SCHEDULE_TIME_OPTIONS.includes(draft.time)) return 'Choose a time on the half hour';
  if (!draft.timezone) return 'Choose a timezone';
  if (draft.frequency === ScheduleFrequency.WEEKLY) {
    return draft.daysOfWeek.length === 0 ? 'Select at least one day of the week' : undefined;
  }
  if (draft.frequency === ScheduleFrequency.BIWEEKLY) {
    if (draft.daysOfWeek.length === 0) return 'Select at least one day of the week';
    return isRealIsoDate(draft.anchorDate) ? undefined : 'Choose a start date';
  }
  if (draft.frequency === ScheduleFrequency.MONTHLY) {
    const day = Number(draft.dayOfMonth);
    return draft.dayOfMonth.trim() !== '' && Number.isInteger(day) && day >= 1 && day <= 31
      ? undefined
      : 'Enter a day of the month from 1 to 31';
  }
  const interval = Number(draft.intervalDays);
  if (draft.intervalDays.trim() === '' || !Number.isInteger(interval) || interval < 1) {
    return 'Enter a whole number of days (1 or more)';
  }
  if (!isRealIsoDate(draft.anchorDate)) return 'Choose a start date';
  return undefined;
};

const formatTime = (time: string): string => {
  const [hour, minute] = time.split(':').map(Number);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 === 0 ? 12 : hour % 12}:${String(minute).padStart(2, '0')} ${suffix}`;
};

const summarizeDays = (days: number[]): string => {
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.length === 7) return 'Daily';
  if (sorted.join() === '1,2,3,4,5') return 'Mon-Fri';
  return sorted.map(d => DAY_OF_WEEK_SHORT_LABELS[d]).join(', ');
};

// e.g. "Mon-Fri at 7:00 AM PT" or "Every 3 days from 2026-10-06 at 7:00 AM PT".
export const summarizeSchedule = (schedule: AuditSchedule): string => {
  const zone =
    TIMEZONE_OPTIONS.find(option => option.value === schedule.timezone)?.shortLabel ??
    schedule.timezone;
  const when = (() => {
    switch (schedule.frequency) {
      case ScheduleFrequency.WEEKLY:
        return summarizeDays(schedule.daysOfWeek ?? []);
      case ScheduleFrequency.BIWEEKLY:
        return `Every 2 weeks on ${summarizeDays(schedule.daysOfWeek ?? [])} from ${schedule.anchorDate}`;
      case ScheduleFrequency.MONTHLY:
        return `Monthly on day ${schedule.dayOfMonth}`;
      default:
        return `Every ${schedule.intervalDays} ${conditionallyPluralize(schedule.intervalDays ?? 0, 'day')} from ${schedule.anchorDate}`;
    }
  })();
  return `${when} at ${formatTime(schedule.time)} ${zone}`;
};

// ---- Reviewers ----

export const normalizeEmail = (email: string): string => email.trim().toLowerCase();

// Adds the emails in `raw` (one, or several separated by commas/semicolons/whitespace) to `list`.
// Duplicates are dropped; anything malformed is returned in `invalid` and not added.
export const addReviewerEmails = (
  list: string[],
  raw: string
): { emails: string[]; invalid: string[] } => {
  const tokens = raw
    .split(/[\s,;]+/)
    .map(normalizeEmail)
    .filter(Boolean);
  const invalid = tokens.filter(token => !isValidEmail(token));
  const emails = [...list];
  tokens.forEach(token => {
    if (isValidEmail(token) && !emails.includes(token)) {
      emails.push(token);
    }
  });
  return { emails, invalid };
};

const invalidReviewerMessage = (list: string[]): string | undefined => {
  const invalid = list.filter(email => !isValidEmail(email));
  return invalid.length > 0 ? `Invalid email: ${invalid.join(', ')}` : undefined;
};

// ---- Model config ----

// Name of the creativity preset whose settings match, or Balanced when none (or none stored) do.
export const creativityFromModelConfig = (model: AuditModelConfig | null): string =>
  Object.entries(CREATIVITY_OPTIONS).find(
    ([, { settings }]) =>
      settings.temperature === model?.temperature &&
      settings.top_k === model?.topK &&
      settings.top_p === model?.topP
  )?.[0] ?? DEFAULT_CREATIVITY;

export const modelConfigFromDraft = (draft: AuditConfigDraft): AuditModelConfig => {
  const { settings } =
    CREATIVITY_OPTIONS[draft.creativity] ?? CREATIVITY_OPTIONS[DEFAULT_CREATIVITY];
  return {
    additionalInstructions: draft.additionalInstructions.trim(),
    instanceUuid: draft.instanceUuid,
    styleGuideUuid: draft.styleGuideUuid,
    temperature: settings.temperature,
    topK: settings.top_k,
    topP: settings.top_p
  };
};

// ---- Draft ----

export const newAuditConfigDraft = (today: string = todayIsoDate()): AuditConfigDraft => ({
  additionalInstructions: '',
  creativity: DEFAULT_CREATIVITY,
  enabled: true,
  externalReviewers: [],
  instanceUuid: '',
  internalReviewers: [],
  lookbackDays: String(DEFAULT_LOOKBACK_DAYS),
  maxAds: String(DEFAULT_MAX_ADS),
  minActiveDays: String(DEFAULT_MIN_ACTIVE_DAYS),
  minConversions: String(DEFAULT_MIN_CONVERSIONS),
  minConversionValue: '',
  minSpend: String(DEFAULT_MIN_SPEND),
  modelEdited: false,
  name: '',
  reviewersEdited: false,
  schedule: { ...scheduleToDraft(), anchorDate: today },
  scheduleEdited: false,
  styleGuideUuid: ''
});

export const draftFromAuditConfig = (config: AuditConfig): AuditConfigDraft => ({
  additionalInstructions: config.modelConfig?.additionalInstructions ?? '',
  configID: config.configID,
  creativity: creativityFromModelConfig(config.modelConfig),
  enabled: config.enabled,
  externalReviewers: config.externalReviewers,
  instanceUuid: config.modelConfig?.instanceUuid ?? '',
  internalReviewers: config.internalReviewers,
  lookbackDays: String(config.lookbackDays),
  maxAds: String(config.maxAds),
  minActiveDays: String(config.minActiveDays),
  minConversions: config.minConversions == null ? '' : String(config.minConversions),
  minConversionValue: config.minConversionValue == null ? '' : String(config.minConversionValue),
  minSpend: String(config.minSpend),
  modelEdited: false,
  name: config.name,
  reviewersEdited: false,
  schedule: scheduleToDraft(config.schedule),
  scheduleEdited: false,
  styleGuideUuid: config.modelConfig?.styleGuideUuid ?? ''
});

const isNonNegativeNumber = (value: string): boolean =>
  value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;

// Conversion limits are "at or below" checks: blank turns the check off, and 0 is not a valid limit.
const isBlankOrPositiveNumber = (value: string): boolean =>
  value.trim() === '' || (Number.isFinite(Number(value)) && Number(value) > 0);

// Returns a field -> message map; empty when the draft is valid. Required: name, spend, active days,
// lookback days, schedule, a model when enabled, and at least one of conversions / conversion value.
export const validateAuditConfigDraft = (
  draft: AuditConfigDraft,
  existingNames: string[] = []
): Partial<Record<keyof AuditConfigDraft, string>> => {
  const errors: Partial<Record<keyof AuditConfigDraft, string>> = {};
  const name = draft.name.trim();
  if (!name) {
    errors.name = 'Name is required';
  } else if (name.length > MAX_CONFIG_NAME_LENGTH) {
    errors.name = `Name must be ${MAX_CONFIG_NAME_LENGTH} characters or fewer`;
  } else if (existingNames.some(existingName => equalsIgnoreCase(existingName, name))) {
    errors.name = 'A config with this name already exists';
  }
  if (!isNonNegativeNumber(draft.minSpend)) {
    errors.minSpend = 'Enter a spend of 0 or more';
  }
  if (!isNonNegativeNumber(draft.minActiveDays) || !Number.isInteger(Number(draft.minActiveDays))) {
    errors.minActiveDays = 'Enter a whole number of days';
  }
  if (
    !isNonNegativeNumber(draft.lookbackDays) ||
    !Number.isInteger(Number(draft.lookbackDays)) ||
    Number(draft.lookbackDays) < 1
  ) {
    errors.lookbackDays = 'Enter a whole number of days (1 or more)';
  }
  if (
    !isNonNegativeNumber(draft.maxAds) ||
    !Number.isInteger(Number(draft.maxAds)) ||
    Number(draft.maxAds) < 1 ||
    Number(draft.maxAds) > MAX_ADS_LIMIT
  ) {
    errors.maxAds = `Enter a whole number from 1 to ${MAX_ADS_LIMIT}`;
  }
  if (!isBlankOrPositiveNumber(draft.minConversions)) {
    errors.minConversions = 'Enter a number greater than 0, or leave blank';
  }
  if (!isBlankOrPositiveNumber(draft.minConversionValue)) {
    errors.minConversionValue = 'Enter a number greater than 0, or leave blank';
  }
  if (draft.minConversions.trim() === '' && draft.minConversionValue.trim() === '') {
    errors.minConversions = 'Set conversions or conversion value (at least one is required)';
  }
  if (draft.enabled && !draft.instanceUuid) {
    errors.instanceUuid = 'Select a model (required for an enabled config)';
  }
  const scheduleError = validateScheduleDraft(draft.schedule);
  if (scheduleError) {
    errors.schedule = scheduleError;
  }
  const internalError = invalidReviewerMessage(draft.internalReviewers);
  if (internalError) {
    errors.internalReviewers = internalError;
  }
  const externalError = invalidReviewerMessage(draft.externalReviewers);
  if (externalError) {
    errors.externalReviewers = externalError;
  }
  return errors;
};

const optionalNumber = (value: string): number | null =>
  value.trim() === '' ? null : Number(value);

// Schedule, reviewers and model config are included on create, or on edit only when the user changed them;
// otherwise they are omitted so the backend keeps the stored values.
export const draftToAuditConfigInput = (draft: AuditConfigDraft): AuditConfigInput => {
  const isCreate = !draft.configID;
  return {
    ...(draft.configID && { configID: draft.configID }),
    enabled: draft.enabled,
    ...((isCreate || draft.reviewersEdited) && {
      externalReviewers: draft.externalReviewers,
      internalReviewers: draft.internalReviewers
    }),
    lookbackDays: Number(draft.lookbackDays),
    maxAds: Number(draft.maxAds),
    minActiveDays: Number(draft.minActiveDays),
    minConversions: optionalNumber(draft.minConversions),
    minConversionValue: optionalNumber(draft.minConversionValue),
    minSpend: Number(draft.minSpend),
    ...((isCreate || draft.modelEdited) && { modelConfig: modelConfigFromDraft(draft) }),
    name: draft.name.trim(),
    ...((isCreate || draft.scheduleEdited) && { schedule: scheduleFromDraft(draft.schedule) })
  };
};

// One-line human summary of a config's rules, e.g.
// "Poor/Average ads · $100+ spend in last 30d · 30+ days active · conv ≤ 0 or value ≤ 0 ·
// Mon-Fri at 7:00 AM PT".
export const summarizeAuditConfig = (config: Omit<AuditConfigInput, 'modelConfig'>): string => {
  const metricRules = [
    config.minConversions == null ? undefined : `conv ≤ ${config.minConversions}`,
    config.minConversionValue == null ? undefined : `value ≤ ${config.minConversionValue}`
  ].filter(Boolean);
  return [
    'Poor/Average ads',
    `$${config.minSpend}+ spend in last ${config.lookbackDays}d`,
    `top ${config.maxAds} ${conditionallyPluralize(config.maxAds, 'ad')}`,
    `${config.minActiveDays}+ ${conditionallyPluralize(config.minActiveDays, 'day')} active`,
    ...(metricRules.length > 0 ? [metricRules.join(' or ')] : []),
    ...(config.schedule ? [summarizeSchedule(config.schedule)] : [])
  ].join(' · ');
};

// Wire <-> UI mapping. Missing rule fields fall back to the product defaults so a config saved
// before a rule existed still renders.
export const auditConfigFromWire = (row: AuditConfigRowWire): AuditConfig => ({
  configID: row.configID,
  createdBy: row.createdBy,
  enabled: row.enabled,
  externalReviewers: row.externalReviewers ?? [],
  internalReviewers: row.internalReviewers ?? [],
  lastModified: row.lastModified,
  lastRunAt: row.lastRunAt,
  lastUser: row.lastUser,
  lookbackDays: row.rules.lookbackDays ?? DEFAULT_LOOKBACK_DAYS,
  maxAds: typeof row.rules.maxAds === 'number' ? row.rules.maxAds : DEFAULT_MAX_ADS,
  minActiveDays: row.rules.minActiveDays ?? DEFAULT_MIN_ACTIVE_DAYS,
  minConversions: row.rules.minConversions ?? null,
  minConversionValue: row.rules.minConversionValue ?? null,
  minSpend: row.rules.minSpend ?? DEFAULT_MIN_SPEND,
  modelConfig: row.modelConfig ?? null,
  name: row.name,
  schedule: row.schedule ?? DEFAULT_SCHEDULE
});

export const auditConfigToWireBody = (config: AuditConfigInput): AuditConfigWireBody => ({
  ...(config.configID && { configID: config.configID }),
  enabled: config.enabled,
  ...(config.externalReviewers && { externalReviewers: config.externalReviewers.join(',') }),
  ...(config.internalReviewers && { internalReviewers: config.internalReviewers }),
  ...(config.modelConfig && { modelConfig: config.modelConfig }),
  name: config.name,
  rules: {
    lookbackDays: config.lookbackDays,
    maxAds: config.maxAds,
    minActiveDays: config.minActiveDays,
    minConversions: config.minConversions,
    minConversionValue: config.minConversionValue,
    minSpend: config.minSpend
  },
  ...(config.schedule && { schedule: config.schedule })
});

// The card's enabled toggle saves only {configID, name, enabled, rules}: no schedule or
// reviewers, so a stale cached row can never overwrite what was saved elsewhere.
export const auditConfigToggleInput = (
  config: AuditConfig,
  enabled: boolean
): AuditConfigInput => ({
  configID: config.configID,
  enabled,
  lookbackDays: config.lookbackDays,
  maxAds: config.maxAds,
  minActiveDays: config.minActiveDays,
  minConversions: config.minConversions,
  minConversionValue: config.minConversionValue,
  minSpend: config.minSpend,
  name: config.name
});
